/**
 * §F4 ticker pill — the glass strip along the bottom of the splash mini-map.
 *
 * The mockup renders four hardcoded `<span>`s side by side under `white-space:nowrap;
 * overflow:hidden`, so on its own 670px half-card the fourth is already clipped and on a
 * narrower one so is the third. That is not a ticker, it is a sentence that runs out of
 * room. So this shows **one line at a time and rotates**, which is what the name promises,
 * fits any width, and lets all five `/audit/ticker` lines actually be read.
 *
 * Nothing here is `dangerouslySetInnerHTML`: the server derives every line from a closed
 * vocabulary (`apps/meta/ticker.py`) and this renders it as text.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { fetchTicker, queryKeys, type TickerLine } from '../../lib/api';

/** How long each line is shown. Long enough to read a short sentence twice. */
export const TICKER_INTERVAL_MS = 6000;

export function Ticker() {
  const { t } = useTranslation();
  const { data } = useQuery<TickerLine[], Error, TickerLine[], ReturnType<typeof queryKeys.ticker>>(
    { queryKey: queryKeys.ticker(), queryFn: fetchTicker },
  );
  const lines = data ?? [];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    // Nothing to rotate through, and — more to the point — no interval left running behind
    // a closed splash. The whole component unmounts with the overlay, but the splash is
    // reopened from the topbar and a leaked timer per open would accumulate.
    if (lines.length < 2) return;
    const id = window.setInterval(
      () => setIndex((i) => (i + 1) % lines.length),
      TICKER_INTERVAL_MS,
    );
    return () => window.clearInterval(id);
  }, [lines.length]);

  // The rotation index outlives the array it indexes: a language switch refetches and the
  // new array may be shorter. Clamping at read time is one line; a `useEffect` that resets
  // it would render one frame of nothing first.
  const current = lines[index % Math.max(lines.length, 1)];
  if (!current) return null;

  return (
    <div
      id="hpTicker"
      className="glass"
      data-testid="splash-ticker"
      /* The content changes on a timer nobody asked for, so it is announced politely or
         not at all — `polite` waits for a screen reader to finish what it is saying. */
      aria-live="polite"
      aria-atomic="true"
    >
      <b>{t('splash.ticker.label')}</b>
      <span className="hp-tick-text">{current.text}</span>
    </div>
  );
}

export default Ticker;
